import { useEffect } from 'react';

import collegeVisitGuideBanner from '@backrs_ui/grow-ui/src/assets/images/college-visit-guide/college-visit-guide-banner.png';
import collegeVisitGuideLogo from '@backrs_ui/grow-ui/src/assets/images/college-visit-guide/college-visit-guide-logo.png';
import collegeVisitOvernight from '@backrs_ui/grow-ui/src/assets/images/college-visit-guide/college-visit-overnight.png';
import {
  GUIDE_TEXT_MAX_WIDTH,
  guideMarkdownToText,
  GuideNumberedTimeline,
  GuidePageShell,
  GuidePrinciplesGrid,
  GuideSections,
  GuideStrategySelector,
  type GuideStrategyPath,
  GuideTaskAction,
  GuideToolsSection,
} from '@backrs_ui/grow-ui/src/components/GuidePage';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import { collegeVisitGuideContent } from '@backrs_ui/grow-ui/src/pages/CollegeVisitGuide/content';
import { StickerCardId } from '@backrs_ui/grow-ui/src/pages/Home/hooks/stickerCardMapping';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import { closeCustomTab } from '@backrs_ui/grow-ui/src/redux/tabs/tabs.slice';

import { Box, SxProps, Theme, useMediaQuery } from '@mui/material';

import { COLLEGE_VISIT_COMPANION_TAB_IDS } from './CollegeVisitCompanionPanel';
import { useAgentContext } from './CollegeVisitGuidePageContainer.agentContext';
import { AsideLabel } from './components/AsideLabel';
import { ChecklistCard } from './components/ChecklistCard';
import { CompanionLinkCards } from './components/CompanionLinkCards';
import { ComparisonBody } from './components/ComparisonCards';
import { ContrastLists } from './components/ContrastLists';
import { EmailTemplateCard } from './components/EmailTemplateCard';
import { ProTipsCarousel } from './components/ProTipsCarousel';
import { SubsectionHeader } from './components/SubsectionHeader';

const COLLEGE_VISIT_GUIDE_TOOL_ID = 'college_visit_guide';

const BEFORE_TOOL_IDS: readonly StickerCardId[] = [
  'college_shortlist',
  'explore_universities_sticker',
  'essay_ideas',
];

const LUBAV_LABEL = 'Discuss with Lubav';

const LUBAV_PROMPTS = {
  before:
    "I am planning college visits. Based on my profile and this Before Your Visit guidance, which of the do/don't items and checklist steps should I prioritize, and why?",
  during:
    'I am about to visit a college campus. Based on this During Your Visit guidance, which high-yield activities should I focus on given my goals, and which common traps am I most at risk of?',
  overnight:
    'Help me decide whether an overnight visit makes sense for me and how to make the most of it, using this Overnight Visits guidance.',
  discernment:
    'Using this discernment framework, help me think through which school types (size, public vs. private, location, research vs. liberal arts, academic culture, social life) fit me best based on what you know about me.',
} as const;

const SECTION_IDS = {
  overview: 'college-visit-section-overview',
  before: 'college-visit-section-before',
  during: 'college-visit-section-during',
  overnight: 'college-visit-section-overnight',
  after: 'college-visit-section-after',
  discernment: 'college-visit-section-discernment',
} as const;

const { header, mindset, before, during, after } = collegeVisitGuideContent;

const COMPARISONS_BY_ID = new Map(
  after.discernment.comparisons.map((comparison) => [comparison.id, comparison]),
);

const DISCERNMENT_SELECTOR_CONTENT = {
  intro: after.discernment.subtitle,
  paths: after.discernment.comparisons.map((comparison) => ({
    id: comparison.id,
    label: comparison.label,
    title: comparison.title,
    icon: comparison.icon,
    moves: [],
  })),
};

const renderComparisonBody = (path: GuideStrategyPath) => {
  const comparison = COMPARISONS_BY_ID.get(path.id);

  return comparison ? (
    <ComparisonBody id={`college-visit-comparison-${comparison.id}`} comparison={comparison} />
  ) : null;
};

const renderComparisonLubavAction = (path: GuideStrategyPath) => {
  const comparison = COMPARISONS_BY_ID.get(path.id);

  return comparison ? (
    <LubavButton
      id={`college-visit-comparison-${comparison.id}-lubav-btn`}
      label={comparison.lubav.label}
      prompt={comparison.lubav.prompt}
      allowTools={false}
    />
  ) : null;
};

const FOLLOW_UP_EMAIL_TEXT = after.followUpEmail.template.map(guideMarkdownToText).join('\n\n');

const MOBILE_MAX_WIDTH = 599;

export const CollegeVisitGuidePageContainer = () => {
  useAgentContext();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery(`(max-width: ${MOBILE_MAX_WIDTH}px)`);

  useEffect(
    () => () => {
      Object.values(COLLEGE_VISIT_COMPANION_TAB_IDS).forEach((tabId) =>
        dispatch(closeCustomTab(tabId)),
      );
    },
    [dispatch],
  );

  const overviewNested = (
    <GuidePrinciplesGrid principles={mindset.fits.slice(0, 2)} card numberChip />
  );
  const overviewExtra = (
    <Box sx={overviewExtraSx}>
      <GuidePrinciplesGrid
        principles={mindset.fits.slice(2)}
        columns={3}
        matchTwoColumnWidth={GUIDE_TEXT_MAX_WIDTH}
        card
        numberChip
      />
    </Box>
  );

  const sections = [
    {
      id: SECTION_IDS.before,
      title: before.title,
      rightSlot: (
        <LubavButton
          id="college-visit-before-lubav-btn"
          label={LUBAV_LABEL}
          prompt={LUBAV_PROMPTS.before}
          allowTools={false}
        />
      ),
      content: (
        <Box sx={sectionBodySx}>
          <Box sx={splitSx}>
            <Box sx={splitMainSx}>
              <SubsectionHeader subtitle={before.admissionsStrategy.subtitle} />
              <ContrastLists
                idPrefix="college-visit-admissions-strategy"
                positive={before.admissionsStrategy.doList}
                negative={before.admissionsStrategy.dontList}
              />
              <Box sx={checklistSpacerSx}>
                <ChecklistCard id="college-visit-before-checklist" checklist={before.checklist} />
              </Box>
            </Box>
            <Box sx={asideSx}>
              <AsideLabel>{before.toolsTitle}</AsideLabel>
              <GuideToolsSection
                toolIds={BEFORE_TOOL_IDS}
                contextLabel="Before your visit tools"
                direction={isMobile ? 'scrollingRow' : 'column'}
              />
            </Box>
          </Box>
          <Box sx={blockSx}>
            <SubsectionHeader title={before.proTips.title} />
            <ProTipsCarousel id="college-visit-pro-tips-carousel" tips={before.proTips.tips} />
          </Box>
        </Box>
      ),
    },
    {
      id: SECTION_IDS.during,
      title: during.title,
      rightSlot: (
        <LubavButton
          id="college-visit-during-lubav-btn"
          label={LUBAV_LABEL}
          prompt={LUBAV_PROMPTS.during}
          allowTools={false}
        />
      ),
      content: (
        <Box sx={splitSx}>
          <Box sx={splitMainSx}>
            <SubsectionHeader subtitle={during.activities.subtitle} />
            <ContrastLists
              idPrefix="college-visit-activities"
              positive={during.activities.highYield}
              negative={during.activities.commonTraps}
            />
          </Box>
          <Box sx={asideSx}>
            <AsideLabel>{during.linksTitle}</AsideLabel>
            <CompanionLinkCards idPrefix="college-visit-during-link" links={during.links} />
          </Box>
        </Box>
      ),
    },
    {
      id: SECTION_IDS.overnight,
      title: during.overnight.title,
      rightSlot: (
        <LubavButton
          id="college-visit-overnight-lubav-btn"
          label={LUBAV_LABEL}
          prompt={LUBAV_PROMPTS.overnight}
          allowTools={false}
        />
      ),
      content: (
        <Box sx={splitSx}>
          <Box sx={splitMainSx}>
            <SubsectionHeader subtitle={during.overnight.subtitle} />
            <GuideNumberedTimeline
              items={during.overnight.steps.map((step) => ({
                id: step.id,
                number: step.number,
                title: step.title,
                points: [step.body],
              }))}
            />
          </Box>
          <Box
            id="college-visit-overnight-image"
            component="img"
            src={collegeVisitOvernight}
            alt=""
            sx={overnightImageSx}
          />
        </Box>
      ),
    },
    {
      id: SECTION_IDS.after,
      title: after.title,
      content: (
        <Box sx={splitSx}>
          <Box sx={splitMainSx}>
            <SubsectionHeader subtitle={after.followUpEmail.subtitle} />
            <Box id="college-visit-follow-up-email-card" sx={afterCardSx}>
              <Box sx={cardHeaderSx}>
                <SubsectionHeader title={after.followUpEmail.title} />
                <GuideTaskAction
                  id="college-visit-follow-up-email-add-task-btn"
                  title={after.followUpEmail.title}
                  description={FOLLOW_UP_EMAIL_TEXT}
                />
              </Box>
              <EmailTemplateCard
                id="college-visit-follow-up-email"
                paragraphs={after.followUpEmail.template}
                copyText={FOLLOW_UP_EMAIL_TEXT}
              />
            </Box>
          </Box>
          <Box sx={asideSx}>
            <AsideLabel>{after.linksTitle}</AsideLabel>
            <CompanionLinkCards idPrefix="college-visit-after-link" links={after.links} />
          </Box>
        </Box>
      ),
    },
    {
      id: SECTION_IDS.discernment,
      title: after.discernment.title,
      rightSlot: (
        <LubavButton
          id="college-visit-discernment-lubav-btn"
          label={LUBAV_LABEL}
          prompt={LUBAV_PROMPTS.discernment}
          allowTools={false}
        />
      ),
      content: (
        <GuideStrategySelector
          idPrefix="college-visit-comparison"
          content={DISCERNMENT_SELECTOR_CONTENT}
          renderCardBody={renderComparisonBody}
          renderCardAction={renderComparisonLubavAction}
        />
      ),
    },
  ];

  return (
    <GuidePageShell
      toolId={COLLEGE_VISIT_GUIDE_TOOL_ID}
      banner={{
        src: collegeVisitGuideBanner,
        alt: header.title,
        overlay: { src: collegeVisitGuideLogo, alt: 'Backrs' },
      }}
    >
      <GuideSections
        header={header}
        overview={{ id: SECTION_IDS.overview, nested: overviewNested, extra: overviewExtra }}
        sections={sections}
      />
    </GuidePageShell>
  );
};

const overviewExtraSx: SxProps<Theme> = ({ backrs }) => ({
  pt: backrs.spacing['spacing-xl'],
});

const sectionBodySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-4xl'],
});

const blockSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const splitSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  '@container (min-width: 720px)': {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
});

const splitMainSx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const cardStyles = ({ backrs }: Theme) => ({
  display: 'flex',
  flexDirection: 'column' as const,
  gap: backrs.spacing['spacing-xl'],
  p: backrs.spacing['spacing-3xl'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const cardHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-lg'],
  flexWrap: 'wrap',
});

const checklistSpacerSx: SxProps<Theme> = ({ backrs }) => ({
  pt: backrs.spacing['spacing-xl'],
});

const afterCardSx: SxProps<Theme> = (theme) => ({
  ...cardStyles(theme),
  borderColor: theme.backrs.colors['Colors/Foreground/fg-quinary'],
});

const overnightImageSx: SxProps<Theme> = ({ backrs }) => ({
  ...asideStyles,
  alignSelf: 'stretch',
  objectFit: 'cover',
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
  '@container (max-width: 719px)': {
    display: 'none',
  },
});

const asideStyles = {
  width: '100%',
  '@container (min-width: 720px)': {
    flexShrink: 0,
    width: '300px',
  },
};

const asideSx: SxProps<Theme> = ({ backrs }) => ({
  ...asideStyles,
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});
